// app/api/api.ts:
import axios from 'axios';

// 创建 axios 实例
const instance = axios.create({
    baseURL: 'https://91ntht.com:8872',
    timeout: 5000,
});

/**
 * 通用响应类型
 * @template T - 响应 data 的类型
 */
export interface ApiResponse<T> {
    code: number;
    data: T;
    msg: string;
}

/**
 * 1. 获取网站基础信息
 * 请求 URL: https://91ntht.com:8872/api/user/findWebsite
 * 示例响应:
 * {
 *   "code": 1,
 *   "data": {
 *     "createBy": "admin",
 *     "updateBy": "admin",
 *     "createTime": "2025-01-05 00:30:48",
 *     "updateTime": "2025-03-21 00:41:39",
 *     "id": 1,
 *     "title": "@NATF标准化职业技能考试认证",
 *     "icon": "image/favicon-20250321124040110.ico",
 *     "logPic": "image/1687918847203565-20250321124022438.png",
 *     "bigPic1": "image/body_1-20250321124102278.png",
 *     "bigPic2": "image/body_2-20250321124104758.png",
 *     "wvNumber": "@NATF001",
 *     "address": "@北京经济技术开发区西环南路18号A座915室",
 *     "copyright": "@Copyright©2023 NATF中青竞赛组委会",
 *     "registrationNumber": "@粤ICP备2023072228号"
 *   },
 *   "msg": "success"
 * }
 */
export interface WebsiteInfo {
    createBy: string;
    updateBy: string;
    createTime: string;
    updateTime: string;
    id: number;
    title: string;
    icon: string;
    logPic: string;
    bigPic1: string;
    bigPic2: string;
    wvNumber: string;
    address: string;
    copyright: string;
    registrationNumber: string;
}

export const getWebsiteInfo = () => {
    return instance.get<ApiResponse<WebsiteInfo>>('/api/user/findWebsite');
};

/**
 * 2. 获取关于我们
 * 请求 URL: https://91ntht.com:8872/api/user/findAboutUs
 * 示例响应:
 * {
 *   "code": 1,
 *   "data": {
 *     "createBy": "admin",
 *     "updateBy": "admin",
 *     "createTime": "2025-01-04 20:53:53",
 *     "updateTime": "2025-03-21 00:03:53",
 *     "id": 1,
 *     "jsonStr": "<p class=\"ql-align-justify\">NATF竞赛是中青研究院创办的竞赛项目，该赛事项目涵盖了数学、物理学、化学、生物学、信息学等多个学科领域。</p>……"
 *   },
 *   "msg": "success"
 * }
 */
export interface AboutUs {
    createBy: string;
    updateBy: string;
    createTime: string;
    updateTime: string;
    id: number;
    jsonStr: string;
}

export const getAboutUs = () => {
    return instance.get<ApiResponse<AboutUs>>('/api/user/findAboutUs');
};

/**
 * 3. 获取首页轮播图
 * 请求 URL: https://91ntht.com:8872/api/user/findSlideshow
 * 示例响应:
 * {
 *   "code": 1,
 *   "data": [
 *     {
 *       "createBy": "admin",
 *       "updateBy": "admin",
 *       "createTime": "2025-03-21 00:42:55",
 *       "updateTime": "2025-03-21 00:43:28",
 *       "id": 10,
 *       "pic": "image/1688347236132167-20250321124254306.jpg",
 *       "number": 0
 *     },
 *     {
 *       "createBy": "admin",
 *       "updateBy": "admin",
 *       "createTime": "2025-03-21 00:43:00",
 *       "updateTime": "2025-03-21 00:43:28",
 *       "id": 11,
 *       "pic": "image/1688347318464485-2025032112430042.jpg",
 *       "number": 1
 *     }
 *   ],
 *   "msg": "success"
 * }
 */
export interface Slideshow {
    createBy: string;
    updateBy: string;
    createTime: string;
    updateTime: string;
    id: number;
    pic: string;
    number: number;
}

export const getSlideshow = () => {
    return instance.get<ApiResponse<Slideshow[]>>('/api/user/findSlideshow');
};

/**
 * 4. 查询证书
 * 请求 URL 示例: https://91ntht.com:8872/api/user/findCertificate?name=1&codeNumber=9
 * 示例响应:
 * {
 *     "code": 1,
 *     "data": {
 *         "createBy": "admin",
 *         "updateBy": "admin",
 *         "createTime": "2025-03-31 17:29:35",
 *         "updateTime": "2025-03-31 17:29:35",
 *         "id": 6,
 *         "avatar": "image/gege-20250331052917515.jpeg",
 *         "name": "111",  // 姓名字段，UI中单独显示
 *         "codeNumber": "222",  // 证书编号字段，UI中单独显示
 *         "codeFile": "image/be455655-19d3-42c8-a2a0-9e5ce73b3587.png",
 *         "jsonStr": "[{\"label\":\"标题333\",\"value\":\"333\"},{\"label\":\"标题444\",\"value\":\"444\"}]",
 *         "formItems": [
 *             {
 *                 "label": "标题333",
 *                 "value": "333"
 *             },
 *             {
 *                 "label": "标题444",
 *                 "value": "444"
 *             }
 *         ]
 *     },
 *     "msg": "success"
 * }
 */
export interface Certificate {
    createBy: string;
    updateBy: string;
    createTime: string;
    updateTime: string;
    id: number;
    avatar?: string | null;
    name: string;
    codeNumber: string;
    codeFile: string;
    jsonStr: string;
    formItems: Array<{
        label: string;
        value: string;
    }>;
}

export const getCertificate = (name: string, codeNumber: string) => {
    return instance.get<ApiResponse<Certificate>>('/api/user/findCertificate', {
        params: {name, codeNumber},
    });
};

/**
 * 5. 获取分栏类别
 * 请求 URL: https://91ntht.com:8872/api/user/findCategoryList
 * 示例响应:
 * {
 *   "code": 1,
 *   "data": [
 *     {
 *       "createBy": "admin",
 *       "updateBy": "admin",
 *       "createTime": "2025-03-20 23:16:22",
 *       "updateTime": "2025-03-20 23:26:10",
 *       "id": 2,
 *       "name": "研究动态",
 *       "labelList": [
 *         "今日头条",
 *         "政策解读",
 *         "教育动态"
 *       ]
 *     },
 *     {
 *       "createBy": "admin",
 *       "updateBy": "admin",
 *       "createTime": "2025-03-20 23:16:52",
 *       "updateTime": "2025-03-20 23:34:12",
 *       "id": 3,
 *       "name": "新闻中心",
 *       "labelList": [
 *         "通知公告",
 *         "决策参考",
 *         "调查研究",
 *         "职业技能"
 *       ]
 *     },
 *     {
 *       "createBy": "admin",
 *       "updateBy": "admin",
 *       "createTime": "2025-03-20 23:19:10",
 *       "updateTime": "2025-03-20 23:19:10",
 *       "id": 4,
 *       "name": "政策法规",
 *       "labelList": null
 *     },
 *     {
 *       "createBy": "admin",
 *       "updateBy": "admin",
 *       "createTime": "2025-03-20 23:19:45",
 *       "updateTime": "2025-03-20 23:19:45",
 *       "id": 5,
 *       "name": "职业技能",
 *       "labelList": null
 *     }
 *   ],
 *   "msg": "success"
 * }
 */
export interface Category {
    createBy: string;
    updateBy: string;
    createTime: string;
    updateTime: string;
    id: number;
    name: string;
    labelList: string[] | null;
}

export const getCategoryList = () => {
    return instance.get<ApiResponse<Category[]>>('/api/user/findCategoryList');
};

/**
 * 6. 获取新闻列表分页
 * 请求 URL 示例:
 * https://91ntht.com:8872/api/user/findArticlePage?page=1&size=10&category=新闻中心&labelName=通知公告
 * 示例响应:
 * {
 *   "code": 1,
 *   "data": {
 *     "content": [
 *       {
 *         "createBy": "admin",
 *         "updateBy": "admin",
 *         "createTime": "2025-03-21 00:06:11",
 *         "updateTime": "2025-03-21 01:41:28",
 *         "id": 1,
 *         "title": "NATF大学生计算机技能竞赛通知",
 *         "count": "66345",
 *         "category": "新闻中心",
 *         "labelStr": "通知公告",
 *         "jsonStr": "<p class=\"ql-align-center\">通知内容，富文本</p>"
 *       }
 *     ],
 *     "pageable": {
 *       "sort": {
 *         "sorted": true,
 *         "unsorted": false,
 *         "empty": false
 *       },
 *       "pageNumber": 0,
 *       "pageSize": 10,
 *       "offset": 0,
 *       "paged": true,
 *       "unpaged": false
 *     },
 *     "last": true,
 *     "totalPages": 1,
 *     "totalElements": 1,
 *     "sort": {
 *       "sorted": true,
 *       "unsorted": false,
 *       "empty": false
 *     },
 *     "first": true,
 *     "numberOfElements": 1,
 *     "size": 10,
 *     "number": 0,
 *     "empty": false
 *   },
 *   "msg": "success"
 * }
 */
export interface Article {
    createBy: string;
    updateBy: string;
    createTime: string;
    updateTime: string;
    id: number;
    title: string;
    count: string;
    category: string;
    labelStr: string;
    jsonStr: string;
}

export interface Sort {
    sorted: boolean;
    unsorted: boolean;
    empty: boolean;
}

export interface Pageable {
    sort: Sort;
    pageNumber: number;
    pageSize: number;
    offset: number;
    paged: boolean;
    unpaged: boolean;
}

export interface ArticlePage {
    content: Article[];
    pageable: Pageable;
    last: boolean;
    totalPages: number;
    totalElements: number;
    sort: Sort;
    first: boolean;
    numberOfElements: number;
    size: number;
    number: number;
    empty: boolean;
}

export const getArticlePage = (
    page: number,
    size: number,
    category: string,
    labelName: string
) => {
    return instance.get<ApiResponse<ArticlePage>>('/api/user/findArticlePage', {
        params: {page, size, category, labelName},
    });
};

/**
 * 7. 获取新闻详情
 * 请求 URL 示例: https://91ntht.com:8872/api/user/findArticle?id=1
 * 示例响应:
 * {
 *   "code": 1,
 *   "data": {
 *     "createBy": "admin",
 *     "updateBy": "admin",
 *     "createTime": "2025-03-21 00:06:11",
 *     "updateTime": "2025-03-21 01:41:28",
 *     "id": 1,
 *     "title": "NATF大学生计算机技能竞赛通知",
 *     "count": "66345",
 *     "category": "新闻中心",
 *     "labelStr": "通知公告",
 *     "jsonStr": "<p class=\"ql-align-center\">通知内容，富文本</p>"
 *   },
 *   "msg": "success"
 * }
 */
export const getArticleDetail = (id: number) => {
    return instance.get<ApiResponse<Article>>('/api/user/findArticle', {
        params: {id},
    });
};

/**
 * 8. 获取友情链接
 * 请求 URL: https://91ntht.com:8872/api/user/findFriendlyLinksList
 * 示例响应:
 * {
 *   "code": 1,
 *   "data": [
 *     {
 *       "createBy": "admin",
 *       "updateBy": "admin",
 *       "createTime": "2025-03-21 01:37:24",
 *       "updateTime": "2025-03-21 01:38:00",
 *       "id": 1,
 *       "name": "中国国家人才网",
 *       "url": "https://www.newjobs.com.cn/",
 *       "number": 0
 *     },
 *     {
 *       "createBy": "admin",
 *       "updateBy": "admin",
 *       "createTime": "2025-03-21 01:37:36",
 *       "updateTime": "2025-03-21 01:38:00",
 *       "id": 2,
 *       "name": "中国青年政治学院",
 *       "url": "https://www.cyu.edu.cn/",
 *       "number": 1
 *     },
 *     {
 *       "createBy": "admin",
 *       "updateBy": "admin",
 *       "createTime": "2025-03-21 01:37:46",
 *       "updateTime": "2025-03-21 01:38:00",
 *       "id": 3,
 *       "name": "中国共青团",
 *       "url": "https://www.gqt.org.cn/",
 *       "number": 2
 *     }
 *   ],
 *   "msg": "success"
 * }
 */
export interface FriendlyLink {
    createBy: string;
    updateBy: string;
    createTime: string;
    updateTime: string;
    id: number;
    name: string;
    url: string;
    number: number;
}

export const getFriendlyLinks = () => {
    return instance.get<ApiResponse<FriendlyLink[]>>('/api/user/findFriendlyLinksList');
};

/**
 * 9. 首页右上角模糊搜索
 * 请求 URL 示例: https://91ntht.com:8872/api/user/findSearch?title=通知
 * 示例响应:
 * {
 *   "code": 1,
 *   "data": [
 *     {
 *       "createBy": "admin",
 *       "updateBy": "admin",
 *       "createTime": "2025-03-21 00:06:11",
 *       "updateTime": "2025-03-21 01:41:28",
 *       "id": 1,
 *       "title": "NATF大学生计算机技能竞赛通知",
 *       "count": "66345",
 *       "category": "新闻中心",
 *       "labelStr": "通知公告",
 *       "jsonStr": "<p class=\"ql-align-center\">通知内容，富文本</p>"
 *     },
 *     {
 *       "createBy": "admin",
 *       "updateBy": "admin",
 *       "createTime": "2025-03-21 00:46:01",
 *       "updateTime": "2025-03-21 01:41:37",
 *       "id": 2,
 *       "title": "学分互认，打破高校知识的"围墙"通知",
 *       "count": "4554",
 *       "category": "研究动态",
 *       "labelStr": "教育动态",
 *       "jsonStr": "<p class=\"ql-align-justify\">近日，中南大学、湖南大学、湖南师范大学三所高校课程互选、学分互认的教学合作模式正式拉开序幕。</p>"
 *     }
 *   ],
 *   "msg": "success"
 * }
 */
export const searchArticles = (title: string) => {
    return instance.get<ApiResponse<Article[]>>('/api/user/findSearch', {
        params: {title},
    });
};

/**
 * 10. 获取实际图片
 * 请求 URL 示例:
 * https://91ntht.com:8872/api/file/image/1688347236132167-20250321124254306.jpg
 * 说明：返回图片的完整 URL，供页面直接使用
 */
export const getFileUrl = (filePath: string | undefined): string => {
    if (!filePath)
        return '';
    return `${instance.defaults.baseURL}/api/file/${filePath}`;
};
